import { REVIEW_HIDE_REASON_MAX } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useModalDialog } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { useHideReview } from '../model/use-hide-review';
import { ReviewConflictNotice } from './review-conflict-notice';

interface HideReviewModalProps {
  readonly review: { readonly id: string; readonly updatedAt: string };
  readonly onClose: () => void;
}

/** 숨김 사유(내부 메모)를 받는 네이티브 모달 — 마운트가 곧 열림이다. */
export const HideReviewModal = ({ review, onClose }: HideReviewModalProps): ReactElement => {
  const [reason, setReason] = useState('');
  const { hide, isPending, error, isConflict, reset } = useHideReview();
  const dialog = useModalDialog(onClose, !isPending);
  const trimmed = reason.trim();

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (trimmed.length === 0 || isPending) return;
    const result = await hide({
      reviewId: review.id,
      reason: trimmed,
      expectedUpdatedAt: review.updatedAt,
    });
    if (result) onClose();
  };

  return (
    <dialog
      ref={dialog.ref}
      aria-label="후기 숨기기"
      aria-modal="true"
      className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-lg border bg-background p-5 text-foreground shadow-lg backdrop:bg-foreground/40"
      onCancel={dialog.onCancel}
      onClose={dialog.onClose}
    >
      <h2 className="font-medium">후기 숨기기</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        숨기면 고객에게 보이지 않습니다. 사유는 내부 기록으로만 남습니다.
      </p>
      <form onSubmit={(event) => void submit(event)} className="mt-4 flex flex-col gap-3">
        <textarea
          aria-label="숨김 사유(내부 메모)"
          className="min-h-28 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          value={reason}
          maxLength={REVIEW_HIDE_REASON_MAX}
          onChange={(event) => setReason(event.target.value)}
        />
        {isConflict ? (
          <ReviewConflictNotice reviewId={review.id} onReloaded={reset} />
        ) : error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" disabled={isPending} onClick={onClose}>
            닫기
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={isPending}
            disabled={trimmed.length === 0 || isPending}
          >
            숨김 처리
          </Button>
        </div>
      </form>
    </dialog>
  );
};
